import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import DefaultImage from '../../../images/admin/default.png';
import Badge from '@material-ui/core/Badge';
import ResponsiveTooltip from '../Shared/ResponsiveTooltip';
import LaptopChromebookIcon from '@material-ui/icons/LaptopChromebook';
import T from 'i18n-react';
import Typography from '@material-ui/core/Typography';
import ExpansionText from '../Expand/ExpansionText';
import Rating from '@material-ui/lab/Rating';
import FavoriteIcon from '@material-ui/icons/Favorite';
import AppAvatar from '../Avatar/AppAvatar';
import { TEXT_SECONDARY } from '../../core/styles/variables';

const useStyles = makeStyles((theme) => ({
  root: {
    borderBottom: `1px dashed ${theme.props.border.primary.main}`,
    padding: theme.spacing(2, 0),
  },
  avatar: {
    width: 50,
    height: 50,
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      width: 60,
      height: 60,
    },
  },
  box: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
  },
  ratingRoot: {
    verticalAlign: 'sub',
  },
  ratingIconFilled: {
    color: theme.props?.palette?.primary?.main || theme.palette.primary.main,
  },
  name: {
    fontWeight: 'bold',
  },
  joinTime: {
    color: TEXT_SECONDARY,
    fontSize: theme.typography.pxToRem(14),
  },
}));

export type ReviewProps = {
  message?: React.ReactNode,
  rating?: number,
  maxRating?: number,
  imageSrc?: string | null,
  name: string,
  online?: boolean,
  joinTime: string,
  textRenderer?: () => React.ReactNode,
  classes?: {
    [key:
      | 'root'
      | 'avatar'
      | 'box'
      | 'ratingRoot'
      | 'ratingIconFilled']: string,
  },
};

const Review = ({
  message,
  rating,
  maxRating,
  imageSrc,
  name,
  joinTime,
  online = false,
  textRenderer,
  ...otherProps
}: ReviewProps) => {
  const classes = useStyles({ classes: otherProps.classes });

  const avatar = (
    <AppAvatar
      loaded
      src={imageSrc || DefaultImage}
      className={classes.avatar}
    />
  );

  return (
    <div className={classes.root}>
      <div className={classes.box}>
        {online ? (
          <Badge
            overlap="circle"
            component="div"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <ResponsiveTooltip
                icon
                contentNode={<LaptopChromebookIcon fontSize="small" />}
                title={T.translate('reviewContainer.online')}
                tooltipProps={{ PopperProps: {} }}
              />
            }
          >
            {avatar}
          </Badge>
        ) : (
          avatar
        )}
        <div>
          {!!name && <Typography className={classes.name}>{name}</Typography>}
          {!!joinTime && <span className={classes.joinTime}>{joinTime}</span>}
        </div>
        {rating != null && (
          <div style={{ marginLeft: 'auto' }}>
            <Rating
              value={rating}
              max={maxRating}
              readOnly
              icon={<FavoriteIcon fontSize="inherit" />}
              size="medium"
              classes={{
                root: classes.ratingRoot,
                iconFilled: classes.ratingIconFilled,
              }}
            />
          </div>
        )}
      </div>
      {textRenderer ? textRenderer() : <ExpansionText>{message}</ExpansionText>}
    </div>
  );
};

export default Review;
